import type { AxisWeights, QueryType, SearchResponse } from '../types';
import { FIELD_KO, ko } from '../vocab';

const TYPE_KO: Record<QueryType, string> = {
  semantic: '의미 검색', color: '색상 검색', mixed: '색상 + 의미 복합',
  structured: '조건 검색', code: '품번 검색', image: '이미지 검색',
  relative: '상대 비교',
};
/** 의미(임베딩) 축이 꺼져 있으면 「의미 검색」이라 부르지 않는다 (리뷰 6회차 m32) */
const TYPE_KO_NO_SEMANTIC: Partial<Record<QueryType, string>> = {
  semantic: '낱말 검색', mixed: '색상 + 낱말 복합',
};

/** §22.2 — 어느 축을 얼마나 반영했는지 사용자에게 보인다. 의미 축이 꺼져 있으면 비활성으로 그린다. */
function AxisBar({ w, semanticOn }: { w: AxisWeights; semanticOn: boolean }) {
  const axes: Array<{ label: string; v: number; off?: boolean }> = [
    { label: semanticOn ? '의미' : '의미 (모델 설치 전)', v: semanticOn ? w.semantic : 0, off: !semanticOn },
    { label: '색상', v: w.color }, { label: '조건', v: w.metadata }, { label: '키워드', v: w.keyword },
  ];
  const max = Math.max(...axes.map((a) => a.v)) || 1;
  return (
    <div className="axes" role="list" aria-label="검색에 반영한 비중">
      {axes.map((a) => (
        <div className={a.off ? 'axis off' : 'axis'} key={a.label} role="listitem">
          <span className="axis-label">{a.label}</span>
          <span className="axis-track" aria-hidden="true">
            {!a.off && <span className="axis-fill" style={{ width: `${(a.v / max) * 100}%`, opacity: a.v === 0 ? 0.15 : 1 }} />}
          </span>
          <span className="sr-only">{a.off ? '꺼짐' : `비중 ${Math.round((a.v / max) * 100)}%`}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * 질의 해석 상자. q 가 비었으면(주소 · 저장한 검색 「검색 열기」 · 단가/새 원단 조건으로 검색어만 비운 경우) 서버는 질의 없이
 * 조건으로만 거르고 **최근 등록순**으로 준다 — 「알아들은 낱말이 없다」 · 축 비중은 사실과 달라(10회차 m48) 고른 조건과 정렬만 말한다.
 */
export default function QueryInterp({ res, q, conds = [] }: { res: SearchResponse; q?: string; conds?: string[] }) {
  if (q !== undefined && !q.trim()) {
    return (
      <div className="interp interp-noq">
        <div className="interp-main">
          {/* 11회차 관찰 ④: 고른 조건도 없으면 「조건만으로」 가 아니다 */}
          <b>{conds.length ? '검색어 없이 조건만으로' : '검색어 · 조건 없이 전체 원단'}</b>
          <span className="interp-type">최근 등록순</span>
          {conds.length > 0 && <div className="chips">{conds.map((c) => <span key={c}>{c}</span>)}</div>}
          <p className="interp-note">
            {conds.length ? '고른 조건에 맞는 원단을' : '전체 원단을'} <b>최근 등록순</b>으로 보여 줍니다 — 비슷한 정도로 매긴 순위가 아니라 카드에 점수를 붙이지 않습니다.
          </p>
        </div>
      </div>
    );
  }
  // 사전에 없는 축 · 값은 칩으로 만들지 않는다 — 영문 원문(purple · luxury …)이 화면에 나가지 않게 (m30)
  const chips = Object.entries(res.interpreted?.filters_inferred ?? {})
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([k, v]) => ({ k, field: FIELD_KO[k], value: ko(v, k, null) }))
    .filter((c): c is { k: string; field: string; value: string } => Boolean(c.field && c.value));

  if (!chips.length && res.query_type === 'code') return null;

  const it = res.interpreted;
  const semanticOn = it?.semantic_active !== false;
  const type = (!semanticOn && TYPE_KO_NO_SEMANTIC[res.query_type]) || TYPE_KO[res.query_type];

  return (
    <div className="interp">
      <div className="interp-main">
        <b>이렇게 이해했습니다</b>
        <span className="interp-type">{type}</span>
        {chips.length > 0 ? (
          <div className="chips">
            {chips.map((c) => <span key={c.k}>{c.field} · {c.value}</span>)}
          </div>
        ) : (
          <p className="interp-none">질의에서 알아들은 낱말(색 · 패턴 · 분위기 · 용도)이 없습니다.</p>
        )}
        {/* R1-1 (2026-10-05): 서버가 실제로 거르는 방식을 그대로 설명한다. 예전 문구("걸러내지 않고
            가점으로만")는 색이 아무 것도 거르지 않던 시절의 것이었다. */}
        <p className="interp-note">
          {it?.gate === 'color' && (
            <>색 이름은 <b>카메라로 측정한 색</b>과 비교해 그 색 계열 원단만 보여 줍니다. 패턴·분위기 같은 나머지 조건은 순위에 반영합니다.</>
          )}
          {it?.gate === 'code_exact' && <>품번이 <b>정확히 일치</b>하는 원단만 보여 줍니다.</>}
          {it?.gate === 'evidence' && (
            <>패턴·분위기·용도 같은 낱말이나 이름·품번 글자가 <b>하나 이상 맞는 원단만</b> 보여 주고, 많이 맞을수록 위에 둡니다.</>
          )}
          {!semanticOn && (chips.length > 0
            ? <> 문장의 뜻을 이해하는 검색 모델은 아직 설치 전이라, 위 낱말로만 찾습니다.</>
            : <> 문장의 뜻을 이해하는 검색 모델은 아직 설치 전이라, 알아들은 낱말이 없으면 이름 · 품번 글자로만 찾습니다.</>)}
        </p>
      </div>
      <AxisBar w={res.weights} semanticOn={semanticOn} />
    </div>
  );
}
